import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBlocker, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Paperclip, Send, Save, X, AlertTriangle, AtSign } from "lucide-react";
import { Badge, Button, Select, Spinner } from "@discordforge/ui";
import {
    accountsAndIdentities,
    attachmentsParam,
    downloadKey,
    message,
    messageUploadAttachments,
    saveMessage,
    sendMessage,
    uploadAttachment,
} from "../api";
import type { Attachment, Identity, Message, MessageDraftParams, StagedAttachment } from "../types";
import { useMail } from "../MailApp";
import { ComposeEditor, type ComposeEditorHandle, type ComposeMode } from "./ComposeEditor";
import { RecipientInput, type RecipientChip } from "./RecipientInput";
import { senderName } from "../utils";

export type ComposeIntent = "new" | "reply" | "replyall" | "forward";

function formatChips(chips: RecipientChip[]): string {
    return chips.map((c) => (c.name ? `${c.name} <${c.email}>` : c.email)).join(", ");
}

export function Compose() {
    const { loginData, folderList, onLogout } = useMail();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const intent = (searchParams.get("mode") ?? "new") as ComposeIntent;
    const srcFolder = searchParams.get("folder") ?? "";
    const srcUid = Number(searchParams.get("uid") ?? 0);

    /* ---- state ---- */
    const [identities, setIdentities] = useState<Identity[]>([]);
    const [identityId, setIdentityId] = useState("");
    const [from, setFrom] = useState(loginData.Email || loginData.mainEmail);

    const [to, setTo] = useState<RecipientChip[]>([]);
    const [cc, setCc] = useState<RecipientChip[]>([]);
    const [bcc, setBcc] = useState<RecipientChip[]>([]);
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [subject, setSubject] = useState("");

    const [attachments, setAttachments] = useState<StagedAttachment[]>([]);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const [sending, setSending] = useState(false);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState<{ kind: "error" | "ok"; text: string } | null>(null);
    const [dirty, setDirty] = useState(false);
    const dirtyRef = useRef(false);
    const markDirty = useCallback(() => {
        dirtyRef.current = true;
        setDirty(true);
    }, []);
    const markClean = useCallback(() => {
        dirtyRef.current = false;
        setDirty(false);
    }, []);

    const [prefillLoading, setPrefillLoading] = useState(intent !== "new");
    const [prefillHtml, setPrefillHtml] = useState("");
    const [draftInfo, setDraftInfo] = useState<string[] | null>(null);
    const [inReplyTo, setInReplyTo] = useState("");
    const [references, setReferences] = useState("");
    const [draftFolder, setDraftFolder] = useState("");
    const [draftUid, setDraftUid] = useState(0);
    const [draftSavedAt, setDraftSavedAt] = useState(0);

    const editorRef = useRef<ComposeEditorHandle>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const editorModeRef = useRef<ComposeMode>("html");
    const signatureAppliedRef = useRef(false);

    /* ---- folders for saveFolder defaults ---- */
    const sentFolder = useMemo(
        () => folderList.find((f) => (f.role ?? "").toLowerCase() === "sent")?.fullName ?? "Sent",
        [folderList]
    );
    const draftsFolder = useMemo(
        () => folderList.find((f) => (f.role ?? "").toLowerCase() === "drafts")?.fullName ?? "Drafts",
        [folderList]
    );

    /* ---- load identities ---- */
    useEffect(() => {
        accountsAndIdentities()
            .then((r) => {
                setIdentities(r.Identities ?? []);
                if (r.Identities?.[0]) {
                    setIdentityId(r.Identities[0].id);
                    if (r.Identities[0].email) setFrom(r.Identities[0].email);
                }
            })
            .catch(() => {});
    }, []);

    const activeIdentity = identities.find((i) => i.id === identityId) ?? identities[0];

    /* ---- signature: apply the active identity's signature once the editor is
     * mounted and the prefill (reply/forward quote) is in place. Mirrors
     * upstream ComposePopupView#setSignature: insert before or after the
     * existing content depending on signatureInsertBefore. ---- */
    useEffect(() => {
        if (signatureAppliedRef.current || prefillLoading || !identities.length) return;
        const ident = identities.find((i) => i.id === identityId) ?? identities[0];
        const raw = ident?.signature ?? "";
        if (!raw) {
            signatureAppliedRef.current = true;
            return;
        }
        const sig = raw.startsWith(":HTML:") ? raw.slice(6) : raw;
        const existing = editorRef.current?.getData() ?? "";
        const html = ident.signatureInsertBefore
            ? `<div>${sig}</div>${existing}`
            : `${existing}${existing ? "<br><br>" : ""}<div>${sig}</div>`;
        editorRef.current?.setContent(html, "html");
        signatureAppliedRef.current = true;
    }, [prefillLoading, identities, identityId]);

    /* ---- prefill from source message (reply/replyall/forward) ---- */
    useEffect(() => {
        if (intent === "new" || !srcFolder || !srcUid) return;
        let cancelled = false;
        message(srcFolder, srcUid, loginData.accountHash)
            .then((m: Message) => {
                if (cancelled) return;
                const exclude = new Set([loginData.Email, loginData.mainEmail]);
                if (intent === "reply") {
                    const recipients = (m.replyTo?.length ? m.replyTo : m.from ?? [])
                        .filter((a) => !exclude.has(a.email))
                        .map((a) => ({ email: a.email, name: a.name || senderName([a]) }));
                    setTo(recipients);
                } else if (intent === "replyall") {
                    const all = [...(m.to ?? []), ...(m.cc ?? [])]
                        .filter((a) => !exclude.has(a.email))
                        .map((a) => ({ email: a.email, name: a.name || undefined }));
                    const fromAddrs = (m.from ?? []).filter((a) => !exclude.has(a.email));
                    setTo([...fromAddrs.map((a) => ({ email: a.email, name: a.name || undefined })), ...all]);
                } else {
                    /* forward: no recipients */
                }
                const subj = m.subject || "";
                setSubject(
                    intent === "forward"
                        ? /^fwd:/i.test(subj)
                            ? subj
                            : `Fwd: ${subj}`
                        : /^re:/i.test(subj)
                          ? subj
                          : `Re: ${subj}`
                );
                setDraftInfo([intent === "forward" ? "forward" : "reply", String(m.uid), m.folder]);
                setInReplyTo(m.messageId ?? "");
                setReferences([m.references, m.messageId].filter(Boolean).join(" ").trim());

                /* attach-from-message: forward → all non-inline attachments;
                 * reply/replyall → only linked ones (referenced in the body,
                 * i.e. have a cid or contentLocation). Upstream behaviour. */
                const atts = (m.attachments?.["@Collection"] ?? []) as Attachment[];
                const toAttach = atts.filter((a) =>
                    intent === "forward" ? !a.isInline : !!(a.cId || a.contentLocation)
                );
                if (toAttach.length) {
                    messageUploadAttachments(toAttach.map((a) => downloadKey(a, loginData.accountHash)))
                        .then((staged) => {
                            if (cancelled) return;
                            const items: StagedAttachment[] = staged
                                .map((s, i) => ({
                                    tempName: s.tempName,
                                    name: toAttach[i]?.fileName ?? "attachment",
                                    mimeType: s.mimeType || toAttach[i]?.mimeType || "application/octet-stream",
                                    size: toAttach[i]?.estimatedSize ?? 0,
                                    progress: 100,
                                }))
                                .filter((s) => !!s.tempName);
                            setAttachments((prev) => [...prev, ...items]);
                        })
                        .catch(() => {}); /* attachment prefill is best-effort */
                }

                /* quote body: reply → blockquote, forward → header + body. Stored in
                 * state and passed to the editor as initialHtml — the editor mounts
                 * only after prefillLoading clears, so ref-based setContent would be
                 * a no-op here. */
                const quotedDate = m.dateTimestamp ? new Date(m.dateTimestamp * 1000).toString() : "";
                const fromLine = formatChips((m.from ?? []).map((a) => ({ email: a.email, name: a.name })));
                if (intent === "forward") {
                    const header = `<p><b>From:</b> ${fromLine}<br><b>Date:</b> ${quotedDate}<br><b>Subject:</b> ${subj}<br><b>To:</b> ${formatChips((m.to ?? []).map((a) => ({ email: a.email, name: a.name })))}</p><br><br>`;
                    const body = m.html
                        ? `<div>${m.html}</div>`
                        : `<div>${(m.plain || "").replace(/\n/g, "<br>")}</div>`;
                    setPrefillHtml(header + body);
                } else {
                    const quoted = m.html
                        ? `<blockquote>${m.html}</blockquote>`
                        : `<blockquote>${(m.plain || "").replace(/\n/g, "<br>")}</blockquote>`;
                    setPrefillHtml(`<br><br><p><b>On ${quotedDate}, ${fromLine} wrote:</b></p>${quoted}`);
                }
                setPrefillLoading(false);
                markDirty(); /* prefilled content counts as unsaved changes */
            })
            .catch((err) => {
                if (!cancelled) {
                    setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to load message" });
                    setPrefillLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intent, srcFolder, srcUid]);

    /* ---- draft body persistence across mode switches handled by editor ---- */

    /* ---- attachments ---- */
    const onFiles = useCallback(async (files: FileList | File[]) => {
        const list = Array.from(files);
        if (!list.length) return;
        setUploading(true);
        setStatus(null);
        for (const file of list) {
            try {
                const staged = await uploadAttachment(file);
                setAttachments((prev) => [...prev, { ...staged, progress: 100 }]);
                markDirty();
            } catch (err) {
                setStatus({
                    kind: "error",
                    text: `Failed to upload ${file.name}: ${err instanceof Error ? err.message : "unknown error"}`,
                });
            }
        }
        setUploading(false);
    }, [markDirty]);

    const removeAttachment = (tempName: string) => {
        setAttachments((prev) => prev.filter((a) => a.tempName !== tempName));
        markDirty();
    };

    /* ---- build the send/save payload ---- */
    const buildParams = useCallback(
        (saveFolder: string, draft: boolean) => {
            const mode = editorRef.current?.getMode() ?? "html";
            const text = editorRef.current?.getData() ?? "";
            const params: MessageDraftParams = {
                identityID: identityId,
                messageFolder: draftFolder,
                messageUid: draftUid,
                saveFolder,
                from: activeIdentity?.name ? `${activeIdentity.name} <${from}>` : from,
                to: formatChips(to),
                cc: formatChips(cc),
                bcc: formatChips(bcc),
                replyTo: "",
                subject,
                draftInfo,
                inReplyTo,
                references,
                markAsImportant: 0,
                attachments: attachmentsParam(attachments),
                dsn: 0,
                requireTLS: 0,
                readReceiptRequest: 0,
                autocrypt: [],
                linkedData: [],
                plain: "",
            };
            if (mode === "html") {
                params.html = text;
                params.plain = "";
            } else {
                params.plain = text;
            }
            void draft;
            return params;
        },
        [identityId, from, activeIdentity, to, cc, bcc, subject, draftInfo, inReplyTo, references, attachments, draftFolder, draftUid]
    );

    /* ---- send ---- */
    const handleSend = async () => {
        if (sending) return;
        if (!to.length && !cc.length && !bcc.length) {
            setStatus({ kind: "error", text: "Add at least one recipient." });
            return;
        }
        setSending(true);
        setStatus(null);
        try {
            await sendMessage(buildParams(sentFolder, false));
            markClean();
            navigate(`/${encodeURIComponent(sentFolder)}`);
        } catch (err) {
            setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to send" });
            setSending(false);
        }
    };

    /* ---- save draft ---- */
    const handleSaveDraft = async () => {
        if (saving) return;
        setSaving(true);
        setStatus(null);
        try {
            const res = await saveMessage(buildParams(draftsFolder, true));
            setDraftFolder(res.folder);
            setDraftUid(res.uid);
            setDraftSavedAt(Date.now());
            setStatus({ kind: "ok", text: "Draft saved" });
            markClean();
        } catch (err) {
            setStatus({ kind: "error", text: err instanceof Error ? err.message : "Failed to save draft" });
        } finally {
            setSaving(false);
        }
    };

    /* ---- keyboard shortcuts ---- */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!e.ctrlKey && !e.metaKey) return;
            if (e.key === "s") {
                e.preventDefault();
                handleSaveDraft();
            } else if (e.key === "Enter") {
                e.preventDefault();
                handleSend();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sending, saving, buildParams, sentFolder, draftsFolder]);

    /* ---- autosave every 60 s (like upstream) when dirty & non-empty ---- */
    const isEmptyForm = useCallback(() => {
        if (to.length || cc.length || bcc.length || subject.trim() || attachments.length) return false;
        const text = editorRef.current?.getData() ?? "";
        return !text || !text.replace(/<[^>]*>/g, "").trim();
    }, [to, cc, bcc, subject, attachments]);

    useEffect(() => {
        const id = setInterval(() => {
            if (dirtyRef.current && !saving && !sending && !isEmptyForm()) {
                handleSaveDraft();
            }
        }, 60 * 1000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saving, sending, isEmptyForm, handleSaveDraft]);

    /* ---- dirty guard: block in-app navigation + tab close ---- */
    const blocker = useBlocker(({ currentLocation, nextLocation }) =>
        dirtyRef.current && currentLocation.pathname !== nextLocation.pathname
    );
    useEffect(() => {
        if (blocker.state === "blocked") {
            if (window.confirm("You have unsaved changes. Discard them and leave?")) blocker.proceed();
            else blocker.reset();
        }
    }, [blocker]);
    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if (dirtyRef.current) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, []);

    /* ---- identity switch: update From + swap the signature ---- */
    const onIdentityChange = (id: string) => {
        const ident = identities.find((i) => i.id === id);
        if (!ident) return;
        setIdentityId(id);
        if (ident.email) setFrom(ident.email);
        if (ident.signature) {
            const sig = ident.signature.startsWith(":HTML:") ? ident.signature.slice(6) : ident.signature;
            const existing = editorRef.current?.getData() ?? "";
            const html = ident.signatureInsertBefore
                ? `<div>${sig}</div>${existing}`
                : `${existing}${existing ? "<br><br>" : ""}<div>${sig}</div>`;
            editorRef.current?.setContent(html, "html");
        }
    };

    if (prefillLoading) {
        return (
            <div className="flex h-screen items-center justify-center bg-[#0b0b0f] text-white">
                <Spinner size={28} label="Loading message…" />
            </div>
        );
    }

    return (
        <div className="flex h-screen flex-col bg-[#0b0b0f] text-white">
            {/* Top bar */}
            <header className="flex shrink-0 items-center gap-3 border-b border-white/10 px-4 py-2.5">
                <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="cursor-pointer rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-white/5 hover:text-white"
                    title="Close"
                >
                    <X size={18} />
                </button>
                <h1 className="text-sm font-black tracking-tight text-white">
                    {intent === "new" ? "New message" : intent === "forward" ? "Forward message" : "Reply"}
                    {dirty && !saving && !sending && (
                        <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-widest text-orange-400">
                            ● unsaved
                        </span>
                    )}
                </h1>

                {status && (
                    <span
                        className={`flex items-center gap-1.5 text-xs font-medium ${
                            status.kind === "ok" ? "text-emerald-400" : "text-red-400"
                        }`}
                    >
                        {status.kind === "ok" ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
                        {status.text}
                        {status.kind === "ok" && draftSavedAt > 0 && (
                            <span className="text-zinc-500">
                                · {new Date(draftSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                        )}
                    </span>
                )}

                <div className="ml-auto flex items-center gap-2">
                    <Button variant="dark" size="sm" onClick={handleSaveDraft} disabled={saving || sending} title="Ctrl+S">
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        Save draft
                    </Button>
                    <Button size="sm" onClick={handleSend} disabled={sending || saving} title="Ctrl+Enter">
                        {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                        Send
                    </Button>
                </div>
            </header>

            {/* Fields */}
            <div className="shrink-0 border-b border-white/10 px-4">
                {/* From + identity */}
                <div className="flex items-center gap-2 border-b border-white/10 py-2">
                    <span className="w-16 shrink-0 text-[11px] font-black uppercase tracking-[0.15em] text-zinc-500">From</span>
                    {identities.length > 1 ? (
                        <div className="min-w-0 flex-1">
                            <Select
                                value={identityId}
                                onChange={onIdentityChange}
                                options={identities.map((i) => ({
                                    value: i.id,
                                    label: i.name ? `${i.name} <${i.email}>` : i.email,
                                }))}
                                className="max-w-md"
                            />
                        </div>
                    ) : (
                        <input
                            type="text"
                            value={from}
                            onChange={(e) => {
                                setFrom(e.target.value);
                                markDirty();
                            }}
                            className="min-w-0 flex-1 bg-transparent py-1 text-sm font-semibold text-zinc-100 focus:outline-none"
                        />
                    )}
                </div>

                <RecipientInput label="To" value={to} onChange={(c) => { setTo(c); markDirty(); }} autoFocus={intent === "new"} />
                {showCc && <RecipientInput label="Cc" value={cc} onChange={(c) => { setCc(c); markDirty(); }} />}
                {showBcc && <RecipientInput label="Bcc" value={bcc} onChange={(c) => { setBcc(c); markDirty(); }} />}

                <div className="flex items-center gap-2 border-b border-white/10 py-2">
                    <span className="w-16 shrink-0 text-[11px] font-black uppercase tracking-[0.15em] text-zinc-500">Subject</span>
                    <input
                        type="text"
                        value={subject}
                        onChange={(e) => {
                            setSubject(e.target.value);
                            markDirty();
                        }}
                        placeholder="Subject"
                        className="min-w-0 flex-1 bg-transparent py-1 text-sm font-semibold text-zinc-100 placeholder:font-normal placeholder:text-zinc-600 focus:outline-none"
                    />
                    {!showCc && (
                        <button
                            type="button"
                            onClick={() => setShowCc(true)}
                            className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-zinc-500 transition-colors hover:text-orange-400"
                        >
                            Cc
                        </button>
                    )}
                    {!showBcc && (
                        <button
                            type="button"
                            onClick={() => setShowBcc(true)}
                            className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-zinc-500 transition-colors hover:text-orange-400"
                        >
                            Bcc
                        </button>
                    )}
                </div>
            </div>

            {/* Editor + attachments */}
            <div className="flex min-h-0 flex-1 flex-col">
                <ComposeEditor
                    ref={editorRef}
                    initialHtml={prefillHtml}
                    onModeChange={(m) => (editorModeRef.current = m)}
                    onInput={markDirty}
                    placeholder="Write your message…"
                />

                {/* Attachments */}
                <div
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        onFiles(e.dataTransfer.files);
                    }}
                    className={`shrink-0 border-t border-white/10 px-4 py-2.5 transition-colors ${
                        dragOver ? "bg-orange-500/10" : ""
                    }`}
                >
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                                if (e.target.files) onFiles(e.target.files);
                                e.target.value = "";
                            }}
                        />
                        <Button
                            variant="dark"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                        >
                            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Paperclip size={13} />}
                            Attach files
                        </Button>
                        <span className="text-[11px] text-zinc-600">or drop them here</span>
                        {attachments.map((a) => (
                            <span
                                key={a.tempName}
                                className="inline-flex max-w-56 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-2.5 py-1.5"
                            >
                                <Paperclip size={11} className="shrink-0 text-orange-400" />
                                <span className="min-w-0">
                                    <span className="block truncate text-xs font-semibold text-zinc-200">{a.name}</span>
                                    <span className="block text-[10px] tabular-nums text-zinc-500">
                                        {a.size >= 1024 * 1024
                                            ? `${(a.size / (1024 * 1024)).toFixed(1)} MiB`
                                            : `${Math.max(1, Math.round(a.size / 1024))} KiB`}
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    onClick={() => removeAttachment(a.tempName)}
                                    className="cursor-pointer rounded p-0.5 text-zinc-500 transition-colors hover:bg-white/10 hover:text-white"
                                    title="Remove"
                                >
                                    <X size={12} />
                                </button>
                            </span>
                        ))}
                        {attachments.length > 0 && (
                            <Badge variant="accent">{attachments.length} attachment{attachments.length > 1 ? "s" : ""}</Badge>
                        )}
                    </div>
                </div>
            </div>

            {/* footer */}
            <footer className="flex shrink-0 items-center gap-3 border-t border-white/10 px-4 py-2 text-[11px] text-zinc-600">
                <span className="flex items-center gap-1.5">
                    <AtSign size={11} />
                    {loginData.Email || loginData.mainEmail}
                </span>
                <button
                    type="button"
                    onClick={onLogout}
                    className="cursor-pointer text-zinc-600 transition-colors hover:text-red-400"
                >
                    Sign out
                </button>
                <span className="ml-auto">Ctrl+Enter to send · Ctrl+S to save draft</span>
            </footer>
        </div>
    );
}
